export enum EmailAnalysisParticipantSide {
  Left = "left",
  Right = "right",
}

export enum EmailAnalysisParticipantType {
  Sender = "sender",
  To = "to",
  Cc = "cc",
  Bcc = "bcc",
}

export enum EmailAnalysisCommunicationType {
  External = "external",
  Internal = "internal",
  Mix = "mix",
}

export enum EmailAnalysisXRayTokenType {
  Token = "token",
  Tpl = "tpl",
}

export enum EmailAnalysisSource {
  GmailApi = "gmailApi",
}

export enum EmailAnalysisTabType {
  Analysis = "analysis",
  XRay = "xRay",
  NextMove = "nextMove",
  Wizard = "wizard",
  Message = "message",
}

export type EmailAnalysisDominantParticipantUnresolved = "unresolved";

export interface EmailAnalysisNextMove {
  value: string | null;
}

export interface EmailAnalysisParticipantMetadata {
  name: string;
  avatarSrc: string | null;
  userId?: string;
  orgId?: string;
  linkedin?: string | null;
}

export interface EmailAnalysisParticipant {
  email: string;
  metadata: EmailAnalysisParticipantMetadata;
  numberEmailsInBranch?: number;
  type?: EmailAnalysisParticipantType;
  isSender?: boolean;
  isUser?: boolean;
}

export interface EmailAnalysisXRayToken {
  index: number;
  positiveUhDirection: boolean;
  ruleId: string;
  textAfter: string;
  textBefore: string;
  token: string;
  type: EmailAnalysisXRayTokenType;
}

export interface EmailAnalysisEmail {
  index: number;
  messageId: string;
  scoreInBranch: number;
  platformMessageId: string;
  participants: {
    leftParticipant: EmailAnalysisParticipant;
    rightParticipant: EmailAnalysisParticipant;
  };
  allEmailParticipants: Omit<EmailAnalysisParticipant, "isSender" | "isUser">[];
  sentAt: string;
  sender: EmailAnalysisParticipantSide;
  sentiment: {
    mag: number;
  };
  communicationType: EmailAnalysisCommunicationType;
  dominantParticipant:
    | EmailAnalysisParticipantSide
    | EmailAnalysisDominantParticipantUnresolved
    | null;
  textSnippet: string;
  html: string | null;
  xRayAnalytics: EmailAnalysisXRayToken[];
  platformThreadId: string;
  platformDraftId: string | null;
  summary: string[];
  upperhand: {
    show: boolean;
  };
}

export interface EmailAnalysisDraft extends EmailAnalysisEmail {
  actualSzScore: number | null;
  messageReplyToId: string | null;
  questionnaireResults: null; // TODO: check if needed
}

export interface EmailAnalysisSimulationFeedback {
  draftFeedback: string;
}

export interface EmailAnalysisAccount {
  accountId: string;
  name: string;
  orgId: string;
  domain: string;
  avatarSrc: string | null;
}

export interface EmailAnalysisEngagement {
  engagementId: string;
}

export interface EmailAnalysisBranch {
  branchId: string;
  parentBranchId: string;
  nextMove: EmailAnalysisNextMove;
  subject: string;
  emails: EmailAnalysisEmail[];
  draft: EmailAnalysisDraft | null;
  participants: EmailAnalysisParticipant[];
  simulationFeedback: EmailAnalysisSimulationFeedback | null;
  account: EmailAnalysisAccount;
  engagement: EmailAnalysisEngagement;
  theFirstReplyIndex: number;
}

export interface EmailAnalysisResult {
  branchResults: EmailAnalysisBranch[];
  platformMessageIdToAnalyze: string;
  isThreadInternal: boolean;
}

export interface EmailAnalysisThreadDetails {
  orgId: string;
  userId: string;
  platformThreadId: string;
  wasAnalyzedBefore: boolean;
  lastEngagementBranchPath: string;
}

export interface EmailAnalysisFeedback {
  feedbackId: string;
  message: string;
}

export interface EmailAnalysisViewedEmail {
  orgId: string;
  userId: string;
  platformThreadId: string;
  platformMessageId: string;
}
